import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Fingerprint, Loader2 } from 'lucide-react';

interface BiometriaLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numeroPedido: string;
  razaoSocial: string;
  isLoading: boolean;
  onConfirm: (links: string[]) => void;
  titulo?: string;
  confirmLabel?: string;
  descricaoExtra?: string;
}

const isLinkValido = (v: string) => /^https?:\/\/\S+$/i.test(v.trim());

export function BiometriaLinksDialog({
  open,
  onOpenChange,
  numeroPedido,
  razaoSocial,
  isLoading,
  onConfirm,
  titulo,
  confirmLabel,
  descricaoExtra,
}: BiometriaLinksDialogProps) {
  const [links, setLinks] = useState<string[]>(['', '', '']);

  useEffect(() => {
    if (!open) setLinks(['', '', '']);
  }, [open]);

  const preenchidos = links.map((l) => l.trim()).filter(Boolean);
  const invalidos = preenchidos.filter((l) => !isLinkValido(l));
  const podeConfirmar = preenchidos.length > 0 && invalidos.length === 0 && !isLoading;

  const setLink = (idx: number, value: string) => {
    setLinks((prev) => prev.map((l, i) => (i === idx ? value : l)));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-brand-rose" />
            {titulo || 'Enviar para Biometria'} — Pedido {numeroPedido}
          </DialogTitle>
          <DialogDescription>
            {razaoSocial}
            <br />
            {descricaoExtra || 'Cole o link da biometria. Se houver reenvios por link expirado, use os espaços 2 e 3. Todos os links ficam registrados na timeline do pedido.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {[0, 1, 2].map((idx) => {
            const valor = links[idx];
            const invalido = valor.trim() !== '' && !isLinkValido(valor);
            return (
              <div key={idx} className="space-y-1">
                <Label htmlFor={`biometria-link-${idx + 1}`} className="text-xs font-semibold">
                  Link {idx + 1}{idx === 0 ? '' : ' (opcional)'}
                </Label>
                <Input
                  id={`biometria-link-${idx + 1}`}
                  placeholder="https://..."
                  value={valor}
                  onChange={(e) => setLink(idx, e.target.value)}
                  disabled={isLoading}
                  className={invalido ? 'border-destructive' : ''}
                />
                {invalido && (
                  <p className="text-[10px] text-destructive">Link inválido — precisa começar com http:// ou https://</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Voltar
          </Button>
          <Button
            className="flex-1 bg-brand-rose hover:bg-muted text-white"
            disabled={!podeConfirmar}
            onClick={() => onConfirm(preenchidos)}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Fingerprint className="h-4 w-4 mr-2" />
                {confirmLabel || 'Enviar p/ Biometria'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
